import { FilterList, QrCode, Search } from '@mui/icons-material';
import { InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import QRLink from '@/components/Modal/Project/QRLink';
import BDHelmet from '@/components/BDHelmet';
import Delete from '@/components/Modal/Project/Delete';
import Share from '@/components/Modal/Project/Share';
import ProjectRow from './ProjectRow';
import Loader from '@/components/ui/Loader/Loader';
import Video from '@/components/Modal/General/Video';
import { AntSwitch } from '@/components/ui/Switch/AntSwitch';
import Copy from '@/components/Modal/Project/Copy';

import { route } from '@/constants/routeName';
import { getStatus } from '@/helper/getStatus';
import base64ToBlob from '@/helper/base64ToBlob';
import useVideoUrl from '@/hooks/useVideoUrl';
import usePatch from '@/hooks/usePatch';

import projectApi from '@/api/project';
import file from '@/api/file';

const fList = [
	{ id: 1, name: 'Dokumenttype' },
	{ id: 2, name: 'Dokumentnummer' },
	{ id: 3, name: 'Dokumentkode' },
	{ id: 4, name: 'Dokumentnavn' },
];

const keyCodes = [17, 18, 78, 69, 68, 67, 83, 39, 37];

const { getProduct, getProducts } = projectApi();

const StatusHistory = ({ uBy, cBy, data, status }) => {
	return (
		<div className='grid grid-cols-12 px-4 py-1 bg-white rounded-sm'>
			<div className='flex flex-col col-span-2 gap-2'>
				<span className='font-bold'>Sist oppdatert</span>
				<span>{format(new Date(data.updatetimestamp), 'yyyy-MM-dd HH:mm:ss')}</span>
			</div>
			<div className='flex flex-col col-span-2 gap-2'>
				<span className='font-bold'>Oppdatert av</span>
				<span>{uBy ? uBy.email?.substring(0, uBy.email.indexOf('@')) : '-'}</span>
			</div>
			<div className='flex flex-col col-span-2 gap-2'>
				<span className='font-bold'>Opprettet</span>
				<span>{format(new Date(data.createdate), 'yyyy-MM-dd HH:mm:ss')}</span>
			</div>
			<div className='flex flex-col col-span-2 gap-2'>
				<span className='font-bold'>Opprettet av</span>
				<span>
					<span>{cBy ? cBy.email?.substring(0, cBy.email.indexOf('@')) : '-'}</span>
				</span>
			</div>
			<div className='flex flex-col col-span-1 gap-2'>
				<span className='font-bold'>Versjon</span>
				<span>{data.productversion && data.productversion !== 'undefined' ? data.productversion : '-'}</span>
			</div>
			<div className='flex flex-col col-span-2 gap-2'>
				<span className='font-bold'>Status</span>
				<span>{status?.title || '-'}</span>
			</div>
			{status?.url && (
				<div className='flex flex-col justify-end col-span-1 gap-2'>
					<img className='w-6 h-6' src={status.url} alt='icon-status' />
				</div>
			)}
		</div>
	);
};

const { getAttachments } = file();

export default function Project() {
	const dispatch = useDispatch();
	const params = useParams();
	const { project, id } = params;
	const navigate = useNavigate();
	const projectDelete = useSelector((state) => state.project.projectDelete);
	const projectShare = useSelector((state) => state.project.projectShare);
	const projectCopy = useSelector((state) => state.project.projectCopy);
	const refetchApi = useSelector((state) => state.trigger.refetch);
	const showVideo = useSelector((state) => state.trigger.showVideo);
	const pStatus = useSelector((state) => state.content.projectStatusD);

	const [filter, setFilter] = useState(2);
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 500);
	const [openQR, setOpenQR] = useState(false);
	const [kc, setKC] = useState({});
	const [pType, setPType] = useState(true);
	const [fileSizeDownload, setFileSizeDownload] = useState(null);
	const [showPopup, setShowPopup] = useState(false);

	const {
		data: pS,
		isFetching: isFetchingSP,
		isFetched: isFetchedSP,
	} = useQuery({
		queryKey: ['project-search', filter, search],
		enabled: !!filter && !!search,
		queryFn: () => getProducts({ search_by: filter, value: search }),
	});
	const dataSearch = useMemo(() => pS?.data?.results || null, [pS]) || null;

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
		refetch,
	} = useQuery({
		queryKey: ['project', project, id],
		enabled: !!project && !!id,
		queryFn: () => getProduct(id),
	});
	const data = useMemo(() => p?.data || null, [p]) || null;

	const dType = data?.productprefixid;
	const isVideo = useMemo(() => data?.productprefix_detail[0].productprefix.toLowerCase().includes('video'), [data]);
	const vUrl = data?.producturl;
	const { videoUrl } = useVideoUrl(dType, isVideo, vUrl);

	const { data: a } = useQuery({
		queryKey: ['file', id],
		queryFn: async () => {
			try {
				const result = await getAttachments(id);
				return result;
			} catch (error) {
				return error;
			}
		},
		enabled: !!id, // Only run query if id exists
	});
	const docs =
		useMemo(() => {
			const res = a?.data?.attachments;
			if (typeof res === 'string') {
				return [];
			}
			const result = a?.data?.attachments || [];
			return result;
		}, [a]) || [];

	const onChangeFilter = (e) => {
		setFilter(e.target.value);
	};

	const handleOnKeyDown = (e) => {
		if (keyCodes.indexOf(e.keyCode) === -1) return;
		setKC((prevState) => {
			const r = { ...prevState };
			r[e.keyCode] = true;
			return r;
		});
	};

	const onClose = (key) => {
		dispatch({ type: 'project/setActionsProject', payload: { key, value: false } });
	};

	const {
		mutate: zipDownload,
		isPending: isPendingZip,
		isSuccess: isSuccessZip,
	} = usePatch({
		url: `downloadlink`,
		method: 'post',
		type: 'form-data',
		callback: async (res) => {
			try {
				const blob = base64ToBlob(res.data.zip_file, 'application/zip');

				const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);

				setFileSizeDownload(`${fileSizeMB}MB`);
				setShowPopup(true);

				const pType = data.productprefix_detail?.length ? data.productprefix_detail[0]?.productprefix : data.productprefix || '';

				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = `${pType} ${data.productcodenumber} ${data.productname} ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}.zip`;
				document.body.appendChild(link);

				setTimeout(() => {
					link.click();
					document.body.removeChild(link);

					setTimeout(() => {
						setShowPopup(false);
						setFileSizeDownload(null);
					}, 3000);
				}, 100);
				// eslint-disable-next-line no-empty
			} catch (error) {}
		},
		onError: (err) => {
			toast.error(`${err.status} - ${err.statusText}`);
		},
	});

	const onZipDownload = () => {
		if (docs.length) {
			const postDownloadFormat = {
				productid: id,
				attachments: JSON.stringify(docs),
				zip_file: true,
			};
			zipDownload(postDownloadFormat);
		}
	};

	useEffect(() => {
		if (kc[17] && kc[18]) {
			if (kc[69] && params.project) {
				// EDIT
				navigate(`/${route.project}/edit/${params.project}/${params.id}`, { replace: false });

				setKC({});
				return;
			}
			if (kc[68]) {
				dispatch({ type: 'project/setActionsProject', payload: { key: 'projectDelete', value: true } });
				setKC({});
				return;
			}
			if (kc[67]) {
				// Copy, Duplicate
				setKC({});
				return;
			}
			if (kc[83]) {
				dispatch({ type: 'project/setActionsProject', payload: { key: 'projectShare', value: true } });
				setKC({});
				return;
			}
			if (kc[39]) {
				// Next Project
				setKC({});
				return;
			}
			if (kc[37]) {
				// Previous
				setKC({});
				return;
			}
			if (kc[78]) {
				// New Document
				navigate(`/${route.project}/add`, { replace: false });
				setKC({});
				return;
			}
		}
	}, [kc, dispatch, navigate, params]);

	useEffect(() => {
		if (refetchApi && refetchApi === 'projectDetail') {
			dispatch({ type: 'trigger/triggerAPI', payload: { key: 'refetch', value: null } });
			refetch();
		}
	}, [refetchApi, dispatch, refetch]);

	// const status = data ? getStatus(+data.status, +data.productprefixid) : null;
	// const uBy = data?.updateby_detail?.length ? data.updateby_detail[0] : '';
	const cBy = data?.createdby_detail?.length ? data.createdby_detail[0] : '';
	const category = data?.category.length ? data.category[0] : null;
	const dRefRel = data?.referencerelation?.filter((r) => !!r.reference_type) || [];
	return (
		<>
			<BDHelmet title='Project' description='BDS Samferdsel project' />
			<div className='w-full h-full' onKeyUp={handleOnKeyDown} tabIndex='0'>
				<div className='container flex flex-col w-full h-full gap-4 pt-8 pb-8 mx-auto focus-visible:outline-none'>
					{((isFetchingP && !isFetchedP) || (isFetchingSP && !isFetchedSP) || (isPendingZip && !isSuccessZip)) && <Loader />}

					{fileSizeDownload && showPopup && (
						<div className='absolute z-50 h-full -translate-x-1/2 top-1/2 left-1/2'>
							<span className=' animate-pulse'>Downloading {fileSizeDownload} file</span>
						</div>
					)}
					{isFetchedP && data && (
						<>
							<div className='flex gap-4'>
								<TextField
									id='search'
									className='w-full input-search'
									placeholder='Søk i dokument her ...'
									onChange={(e) => {
										setSearch(e.target.value);
									}}
									size='small'
									InputProps={{
										startAdornment: (
											<InputAdornment position='start'>
												<Search style={{ color: 'black' }} />
											</InputAdornment>
										),
									}}
								/>
								<Select
									labelId='filter'
									className='w-auto min-w-48'
									value={filter}
									onChange={onChangeFilter}
									IconComponent={() => <FilterList sx={{ color: 'var(--primary)' }} />}
									size='small'
								>
									<MenuItem value=''>
										<em className='text-gray-300'>--Filter by--</em>
									</MenuItem>
									{fList?.map((p) => (
										<MenuItem key={p.id} value={p.id} name={p.name}>
											{p.name}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center'>
								{category?.group_name && (
									<>
										<span>{category.group_name}</span>
										<ChevronRight size={16} />
									</>
								)}
								{category?.category_name && (
									<>
										<span>{category.category_name}</span>
										<ChevronRight size={16} />
									</>
								)}
								<span className='select-text'>
									{data.productprefix_detail[0]?.productprefix} {data.productcodenumber}
								</span>
							</div>
							<div className='flex items-center justify-between'>
								<div className='flex flex-col gap-4'>
									{/* <span className='font-bold text-bd-blue'>
										{data.productprefix_detail[0]?.productprefix} - {data.productcodenumber}
									</span> */}
									<h1 className='text-4xl select-text'>{data.productname}</h1>
								</div>
								{data?.version_list.length > 0 && (
									<div className='flex flex-col justify-end gap-1'>
										<span className='text-right'>Versjon</span>
										<Select
											defaultValue={id}
											labelId='filter'
											className='w-auto bg-white min-w-48'
											label=''
											size='small'
											placeholder='--Select versjon--'
											onChange={(e) => {
												const val = e.target.value;
												if (val && val !== +id) {
													navigate(`/${route.project}/${project}/${val}`, { replace: false });
												}
											}}
										>
											<MenuItem value=''>
												<em className='text-gray-300'>--Select versjon--</em>
											</MenuItem>
											{data?.version_list?.map((p) => (
												<MenuItem key={p.productid} value={p.productid} name={p.productid.name}>
													{p.productversion}
												</MenuItem>
											))}
										</Select>
									</div>
								)}
							</div>
							<p className='px-4 py-1 bg-white border border-black rounded-sm'>{data.description || '-'}</p>
							<div className='flex items-center justify-between'>
								<span>Produktrelasjonsliste</span>
								<div className='flex gap-4'>
									{vUrl && videoUrl?.url && (
										<button
											className='flex gap-1 btn'
											onClick={() => {
												dispatch({
													type: 'trigger/triggerAPI',
													payload: { key: 'showVideo', value: { name: data.productname, number: data.productcodenumber, ...videoUrl } },
												});
											}}
										>
											Se Video
										</button>
									)}
									<button
										className='w-auto btn'
										onClick={onZipDownload}
										disabled={!docs.length}
									>
										Zip Download
									</button>
									<button className='flex gap-1 btn' onClick={() => setOpenQR(true)}>
										Se QR Code
										<QrCode />
									</button>
								</div>
							</div>
							<div>
								<div className='grid w-full grid-cols-12 py-2 text-sm text-gray-500 bg-white'>
									<div className='relative col-span-2 text-center'>
										<div className='absolute top-[2px] left-2'>
											{!search && (
												<AntSwitch
													checked={pType}
													onChange={(e) => setPType(e.target.checked)}
													className='switch-default'
													aria-label='standard'
													size='medium'
												/>
											)}
										</div>
										Type
									</div>
									<div className='col-span-1 text-left'>Nummer</div>
									<div className='col-span-3 text-left'>Navn</div>
									<div className='col-span-1 text-center'>Versjon</div>
									<div className='col-span-1 text-center'>Type</div>
									<div className='col-span-1 text-center'>Status</div>
									<div className='col-span-3 text-center'>link</div>
								</div>
								{/* <Table>
									<TableHeader>
										<TableRow>
											<TableHead>Type</TableHead>
											<TableHead>Nummer</TableHead>
											<TableHead>Navn</TableHead>
											<TableHead>Versjon</TableHead>
											<TableHead></TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										<TableRow></TableRow>

										{project ? (
											<TableRow>
												<TableCell align='right'></TableCell>
											</TableRow>
										) : (
											<TableRow>
												<TableCell colSpan={5} className='h-24 text-center'>
													No results.
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table> */}
								<div id='relations' className='w-full'>
									{filter && search && (
										<>
											{dataSearch?.length === 0 && <span className='flex justify-center w-full p-4 mx-auto'>No data</span>}
											{dataSearch?.length > 0 &&
												dataSearch.map((d) => {
													return <ProjectRow key={d.productid} project={d} idx={0} type={'search'} />;
												})}
										</>
									)}
									{!search && <ProjectRow project={data} idx={0} type={pType ? 'child' : 'parent'} />}
								</div>
							</div>

							{data?.article?.length > 0 && (
								<div className='grid grid-cols-12 gap-1 bg-white rounded-sm'>
									{data.article
										.filter((a) => !!a.articlenumber)
										.map((a) => {
											return (
												<div key={a.id} className='flex col-span-6 gap-2 px-2 py-1 font-bold'>
													<span>Artikkel nummer:</span>
													<span>{a.articlenumber}</span>
												</div>
											);
										})}
								</div>
							)}

							{dRefRel?.length > 0 && (
								<div className='grid grid-cols-12 gap-1 bg-white rounded-sm'>
									{dRefRel.map((a) => {
										return (
											<div key={a.id} className='flex col-span-6 gap-2 px-2 py-1 font-bold'>
												<span>{a.reference_type}:</span>
												<span>{a.reference_detail}</span>
											</div>
										);
									})}
								</div>
							)}

							{/* {data && <StatusHistory uBy={uBy} cBy={cBy} data={data} status={status} />} */}
							{data &&
								data.status_history_list?.length > 0 &&
								data.status_history_list
									.sort((a, b) => new Date(b.updatetimestamp) - new Date(a.updatetimestamp))
									.map((d) => {
										const dt = { ...d, createdate: data.createdate };
										const uBy = { email: d.updatedby_email };
										const nStatus = getStatus(pStatus, +d.status);
										return <StatusHistory key={d.id} uBy={uBy} cBy={cBy} data={dt} status={nStatus} />;
									})}
						</>
					)}
				</div>
			</div>
			{openQR && <QRLink open={!!openQR} onClose={() => setOpenQR(false)} project={data} />}
			{projectDelete && <Delete open={!!projectDelete} id={id} project={data} onClose={() => onClose('projectDelete')} />}
			{projectCopy && <Copy open={!!projectCopy} id={id} project={data} onClose={() => onClose('projectCopy')} />}
			{projectShare && <Share open={!!projectShare} onClose={() => onClose('projectShare')} />}
			{showVideo && (
				<Video
					open={!!showVideo}
					video={showVideo}
					onClose={() => dispatch({ type: 'trigger/triggerAPI', payload: { key: 'showVideo', value: null } })}
				/>
			)}
		</>
	);
}
